import type { FC, ReactNode } from 'react'
import type { QuoteItem } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useToast } from '@/components/generals/toast'
import { QUOTE_LIST_LIMIT } from '@/constants/quote-form'

const STORAGE_KEY = 'liftline-quote-list'

interface QuoteListValue {
    items: QuoteItem[]
    has: (key: string) => boolean
    add: (item: QuoteItem) => boolean
    remove: (key: string) => void
    toggle: (item: QuoteItem) => void
    clear: () => void
    isOpen: boolean
    setOpen: (open: boolean) => void
}

const QuoteListContext = createContext<QuoteListValue | null>(null)

export const useQuoteList = (): QuoteListValue => {
    const value = useContext(QuoteListContext)

    if (!value) throw new Error('useQuoteList must be used inside QuoteListProvider')

    return value
}

const persist = (items: QuoteItem[]) => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    }
    catch {
        // Storage can be unavailable in private modes; the list still works for this page view.
    }
}

interface QuoteListProviderProps {
    children: ReactNode
}

export const QuoteListProvider: FC<QuoteListProviderProps> = ({ children }): ReactNode => {
    const { notify } = useToast()
    const [items, setItems] = useState<QuoteItem[]>([])
    const [isOpen, setOpen] = useState(false)
    const itemsRef = useRef<QuoteItem[]>([])

    useEffect(() => {
        try {
            const saved = window.sessionStorage.getItem(STORAGE_KEY)

            if (saved) {
                const parsed = JSON.parse(saved) as QuoteItem[]

                if (Array.isArray(parsed)) {
                    itemsRef.current = parsed.slice(0, QUOTE_LIST_LIMIT)
                    setItems(itemsRef.current)
                }
            }
        }
        catch {
            // Ignore unreadable saved lists.
        }
    }, [])

    const commit = useCallback((next: QuoteItem[]) => {
        itemsRef.current = next
        setItems(next)
        persist(next)
    }, [])

    const has = useCallback((key: string) => items.some(item => item.key === key), [items])

    const add = useCallback((item: QuoteItem) => {
        const current = itemsRef.current

        if (current.some(existing => existing.key === item.key)) return true

        if (current.length >= QUOTE_LIST_LIMIT) {
            notify(`Your list holds ${QUOTE_LIST_LIMIT} items at most. Take one out to make room.`)

            return false
        }

        commit([...current, item])
        notify(
            <>
                Saved for your quote: <b className='font-semibold'>{item.title}</b>
            </>
        )

        return true
    }, [commit, notify])

    const remove = useCallback((key: string) => {
        commit(itemsRef.current.filter(item => item.key !== key))
    }, [commit])

    const toggle = useCallback((item: QuoteItem) => {
        if (itemsRef.current.some(existing => existing.key === item.key)) {
            remove(item.key)

            return
        }

        add(item)
    }, [add, remove])

    const clear = useCallback(() => commit([]), [commit])

    const value = useMemo(() => ({
        items,
        has,
        add,
        remove,
        toggle,
        clear,
        isOpen,
        setOpen
    }), [items, has, add, remove, toggle, clear, isOpen])

    return (
        <QuoteListContext.Provider value={value}>
            {children}
        </QuoteListContext.Provider>
    )
}
